import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SidebarComponent } from '../../../components/sidebar/sidebar.component';
import { MobileHeaderComponent } from '../../../components/mobile-header/mobile-header.component';
import { FormGroup, FormControl, Validators, ReactiveFormsModule, ValidatorFn, AbstractControl } from '@angular/forms';
import { spanishProvinces } from '../../../utils/provinces';
import { europeanUnionCountries } from '../../../utils/european-union-countries';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { MatButtonModule } from '@angular/material/button';
import { getCookie } from '../../../utils/getCookies';
import { skipFormValidation } from '../../../utils/dev-flags';
import { endDateNotBeforeStart } from '../../../utils/date-range.validator';
import { commonEuropeanLanguages } from '../../../utils/languages';
import { languageLevels } from '../../../utils/language-levels';
import { Router } from '@angular/router';
import { FooterComponent } from '../../../components/footer/footer.component';
import { EditExperienceModalComponent } from './modals/edit-experience-modal/edit-experience-modal.component';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { EditEducationModalComponent } from './modals/edit-education-modal/edit-education-modal.component';
import { EditLanguageModalComponent } from './modals/edit-language-modal/edit-language-modal.component';
import { skillLevels } from '../../../utils/skillLevels';
import { EditTechnicalSkillsModalComponent } from './modals/edit-technical-skills-modal/edit-technical-skills-modal.component';
import { TranslateModule } from '@ngx-translate/core';
import { softSkills } from '../../../utils/soft-skills';
import { SidebarUserComponent } from '../../../components/sidebar-user/sidebar-user.component';
import { FlowStepperComponent } from '../../../components/flow-stepper/flow-stepper.component';
import { LanguageSwitcherComponent } from '../../../components/language-switcher/language-switcher.component';

// Component definition for the manual registration process
// Looks like the rest of the sign-up flow: it reuses the profiling page styles (top bar, title, cards, inputs)
@Component({
  host: { class: 'conectio-brand' },
  selector: 'app-manual-register',
  standalone: true,
  imports: [FlowStepperComponent, CommonModule, SidebarComponent, MobileHeaderComponent, ReactiveFormsModule, MatButtonModule, FooterComponent,
    SidebarUserComponent, TranslateModule, LanguageSwitcherComponent],
  templateUrl: './manual-register.component.html',
  styleUrls: ['../../candidate-profiling/candidate-profiling.component.scss', './manual-register.component.scss']
})
export class ManualRegisterComponent {

    // Custom validator to check if passwords match
    passwordMatchValidator: ValidatorFn = (control: AbstractControl): { [key: string]: boolean } | null => {
        const formGroup = control as FormGroup;
        const password = formGroup.get('password')?.value;
        const confirmPassword = formGroup.get('confirmPassword')?.value;

        // Only validate if both password fields have values
        if (password && confirmPassword) {
            return password === confirmPassword ? null : { mismatch: true };
        }
        return null; // Return null if either field is empty
    };

    // Custom validator to check if the date is valid
    dateValidator: ValidatorFn = (control: AbstractControl): { [key: string]: boolean } | null => {
        const dateValue = control.value;
        const year = new Date(dateValue).getFullYear();
        return isNaN(year) || year.toString().length !== 4 ? { invalidDate: true } : null;
    };

    // Custom validator to check if the end date is greater than the start date
    endDateValidator: ValidatorFn = (control: AbstractControl): { [key: string]: boolean } | null => {

        if (control.value === null || control.value === undefined || control.value === '') {
            // We need to return true, as the end date is optional
            return { invalidEndDate: true };
        }
        
        const dateValue = control.value;
        const year = new Date(dateValue).getFullYear();

        // Check if the date is valid
        if (isNaN(year) || year.toString().length !== 4) {
            return { invalidDate: true };
        }

        // Check if the end date is greater than the start date
        const startDate = control.parent?.get('startDate')?.value;
        const endDate = control.value;

        if (startDate && endDate && new Date(endDate) < new Date(startDate)) {
            return { endDateBeforeStartDate: true };
        }

        return null;
    };

    // Initialization of component properties
    // This page shows steps 1-4; step 5 (preferences) is the profiling page, reached by finishing step 4
    step: number = Math.min(Number(getCookie('manualStep')) || 1, 4);
    spanishProvinces: string[] = spanishProvinces;
    europeanUnionCountries: string[] = europeanUnionCountries;
    languages: any[] = commonEuropeanLanguages;
    languageLevels: any[] = languageLevels;
    softSkills: any[] = softSkills;
    loading: boolean = false;
    experiences: any[] = [];
    errorCode: string = '';
    educations: any[] = [];
    addedLanguages: any[] = [];
    addedSoftSkills: any[] = [];
    addedTechnicalSkills: any[] = [];
    missingLanguagesError: boolean = false;
    missingSoftSkillsError: boolean = false;
    missingTechnicalSkillsError: boolean = false;
    missingEducationError: boolean = false;
    experiencesError: boolean = false;
    editExperienceModal: any;
    step2UndefinedError: boolean = false;
    skillLevels: any[] = skillLevels;
    experienceError: boolean = false;
    educationsError: boolean = false;
    errorSkills: boolean = false;
    educationsFormError: boolean = false;
    // "Save and continue later" was used on this step: show a short confirmation
    progressSaved: boolean = false;
    // A save failed (server or connection): show a message instead of leaving the button silent
    saveError: boolean = false;

    // Edit dialogs share the brand look of the flow
    private readonly editDialogConfig: MatDialogConfig = {
        panelClass: 'conectio-brand',
        width: '560px',
        maxWidth: 'calc(100vw - 32px)'
    };

    // Native selects: treat an empty placeholder ('') and a reset control (null) as the same value
    sameValue = (a: any, b: any) => (a ?? '') === (b ?? '');

    get firstName(): string {
        return (getCookie('name') || '').split(' ')[0];
    }

    get hasOffer(): boolean {
        return !!getCookie('offer');
    }
    // Form group for managing experience input fields
    experienceForm: FormGroup = new FormGroup({
        role: new FormControl('', []),
        company: new FormControl('', []),
        startDate: new FormControl('', [this.dateValidator]),
        endDate: new FormControl(''),
        responsibilities: new FormControl('', [])
    }, { validators: endDateNotBeforeStart });

    // Form group for managing registration input fields
    registerForm: FormGroup = new FormGroup({
        email: new FormControl('', [Validators.required, Validators.email]),
        linkedin_url: new FormControl('', [
            Validators.required,
            Validators.pattern(/^https?:\/\/(www\.)?linkedin\.com\/.*$/i)
        ]),
        country: new FormControl('', [Validators.required]),
        region: new FormControl('', [Validators.required]),
        password: new FormControl('', [Validators.required]),
        confirmPassword: new FormControl('', [Validators.required])
    }, { validators: this.passwordMatchValidator }); // Add custom validator

    educationForm: FormGroup = new FormGroup({
        degree: new FormControl('', []),
        institution: new FormControl('', []),
        startDate: new FormControl('', [this.dateValidator]),
        endDate: new FormControl('')    
    }, { validators: endDateNotBeforeStart });

    skillsForm: FormGroup = new FormGroup({
        language: new FormControl('', [Validators.required]),
        languageLevel: new FormControl('', [Validators.required])
    }); 

    softSkillsForm: FormGroup = new FormGroup({
        softSkill: new FormControl('', [Validators.required]),
        softSkillLevel: new FormControl('', [Validators.required])
    }); 

    technicalSkillsForm: FormGroup = new FormGroup({
        technicalSkill: new FormControl('', [Validators.required]),
        technicalSkillLevel: new FormControl('', [Validators.required])
    });  

    //********EDITING FORMS********//
    // Form group for editing experience
    editExperience: FormGroup = new FormGroup({
        role: new FormControl('', []),
        company: new FormControl('', []),
        startDate: new FormControl('', [this.dateValidator]),
        endDate: new FormControl(''),
        responsibilities: new FormControl('', [])
    });


    // Constructor to initialize the component and fetch user experiences
    constructor(private http: HttpClient, private router: Router, public dialog: MatDialog) {
        // Subscribe to form value changes for debugging
        // this.registerForm.valueChanges.subscribe(() => {
        //     console.log(this.registerForm.value);
        // });

        // Fetch user experiences from the API
        this.http.get(`${environment.url}/api/job-experience/user/`, {
            headers: {
                'Authorization': `Bearer ${getCookie('access_token')}`
            }
        }).subscribe({
            next: (data: any) => {
                this.experiences = data; // Store the retrieved experiences
                console.log('Retrieved experiences:', this.experiences);

            },
            error: (error) => {
                console.error('Error fetching experiences:', error);
            }
        });

        // Fetch user education from the API
        this.http.get(`${environment.url}/api/education`, {
            headers: {
                'Authorization': `Bearer ${getCookie('access_token')}`
            }
        }).subscribe({
            next: (data: any) => {
                this.educations = data; // Store the retrieved education
                console.log('Retrieved education:', this.educations);
            },
            error: (error) => {
                console.error('Error fetching education:', error);
            }
        });

        this.http.get(`${environment.url}/api/skills`, {
            headers: {
                'Authorization': `Bearer ${getCookie('access_token')}`
            }
        }).subscribe({
            next: (data: any) => { 
                 // Use 'any' instead of 'any[]' to avoid type errors
                this.addedLanguages = data.languages.map((lang: any) => {
                    const language = commonEuropeanLanguages.find(l => l.id === parseInt(lang.language));
                    const languageLevel = languageLevels.find(l => l.id === parseInt(lang.level)); // Assuming level is already in the desired format
                    return {
                        language: language, // Get the name from commonEuropeanLanguages
                        languageLevel: languageLevel // Assuming level has a name property
                    };
                });

                //Get soft skills
                this.addedSoftSkills = data.softSkills.map((skill: any) => {
                    const softSkill = softSkills.find(s => s.id === parseInt(skill.skill));
                    const softSkillLevel = skillLevels.find(l => l.level === skill.level) ?? skillLevels.find(l => l.level === 'expert');
                    return {
                        softSkill: softSkill, // Get the name from commonEuropeanLanguages
                        softSkillLevel: softSkillLevel // Assuming level has a name property
                    };
                });



                //Get technical skills
                this.addedTechnicalSkills = data.technicalKnowledge.map((skill: any) => {
                    const technicalSkillLevel = skillLevels.find(l => l.level === skill.level);
                    return {
                        technicalSkill: skill.technicalSkill, // Get the name from commonEuropeanLanguages
                        technicalSkillLevel: technicalSkillLevel // Assuming level has a name property
                    };
                });
                if (data.step_register && data.step_register > 0 && data.step_register <= 5) {
                    let step_register = data.step_register;
                    // Going back to an earlier step (e.g. from the profiling page's step bar) keeps that step
                    const cookieStep = Number(getCookie('manualStep'));
                    if (cookieStep >= 1 && cookieStep < step_register) {
                        step_register = cookieStep;
                    }
                    // Opening this page always shows a manual step: someone who already reached step 5 sees step 4
                    step_register = Math.min(step_register, 4);
                  document.cookie = `manualStep=${step_register}; path=/;`;
                  this.step = step_register;
                }
                else {
                    this.step = Number(getCookie('manualStep')) || 1;
                    document.cookie = `manualStep=${this.step}; path=/;`;
                    
                }
                console.log('Retrieved languages:', this.languages);
            },
            error: (error: any) => {
                console.error('Error fetching languages:', error);
            }
        });
    }

    // Method to proceed to the next step in the registration process
    nextStep1(goNext: boolean) {
        this.loading = true; // Set loading state to true
        const accessToken = getCookie('access_token'); // Extract access token from cookie
        this.errorCode = '';

        if (this.registerForm.valid || skipFormValidation) { // Check if the registration form is valid
            const formData = this.registerForm.value; // Get form data

            // Prepare payload for registration API
            const payload = {
                name: getCookie('name') || '', // Get name from cookie
                phone: getCookie('phone') || '', // Get phone from cookie
                email: formData.email, // Email from form
                confirm_email: formData.confirmEmail, // Confirm email from form
                country: formData.country, // Country from form
                region: formData.region, // Region from form
                password: formData.password, // Password from form
                confirm_password: formData.confirmPassword, // Confirm password from form
                linkedin_url: formData.linkedin_url // LinkedIn URL from form
            };

            // Send registration request to the API
            this.http.post(`${environment.url}/api/register`, payload, {
                headers: {
                    'Content-Type': 'application/json', // Set content type to JSON
                    'Authorization': `Bearer ${accessToken}` // Use access token as authorization header
                }
            })
            .subscribe({
                next: (data: any) => {
                    this.loading = false; // Set loading state to false
                    if (data.error) {
                        console.error('Error:', data.error); // Log any errors from the response
                    } else {
                        console.log('Success:', data.message); // Log success message
                        if (goNext) { // If save is true, proceed to the next step
                            window.scrollTo({
                                top: 0,
                                behavior: 'smooth' // Smooth scroll to the top
                            });
                            
                            this.step++; // Increment step
                            this.progressSaved = false;
                            document.cookie = "manualStep=" + this.step + "; path=/"; // Update step in cookie to be accessible from any page
                            document.cookie = "access_token=" + data.access_token + "; path=/"; // Update step in cookie to be accessible from any page
                        }
                    }
                },
                error: (error) => {
                    this.loading = false; // Set loading state to false
                    this.errorCode = error?.error?.code || 'UNKNOWN'; // Store error code
                    console.error('Error:', error); // Log any errors
                    window.scrollTo({
                        top: 0,
                        behavior: 'smooth' // Smooth scroll to the top
                    });
                }
            });
        } else {
            console.error('Form is invalid'); // Log if the form is invalid
            this.markAllAsTouched(this.registerForm); // Mark all fields as touched for validation

            this.loading = false; // Set loading state to false
        }
    }

    // Method to add a new experience to the list
    addExperience() {
        if (this.experienceForm.valid) { // Check if the experience form is valid
            const experienceData = {
                role: this.experienceForm.get('role')?.value,
                company: this.experienceForm.get('company')?.value,
                start_date: this.experienceForm.get('startDate')?.value,
                end_date: this.experienceForm.get('endDate')?.value || null, // Treat as ongoing if omitted
                responsibilities: this.experienceForm.get('responsibilities')?.value
            };

            this.experiences.push(experienceData); // Add to local state if valid
            this.clearForm(this.experienceForm); // Clear the form values
            console.log('Experience added to local state:', experienceData); // Log added experience
            this.experienceError = false;
            return true;
        } else {
            console.error('Experience form is invalid'); // Log if the experience form is invalid
            this.experienceError = true;
            this.markAllAsTouched(this.experienceForm); // Mark all fields as touched for validation
            return false;
        }
    }

    // Method to proceed to the next step in the experience process
    nextStep2(goNext: boolean) {
        const accessToken = getCookie('access_token'); // Extract access token from cookie
        this.loading = true; // Set loading state to true
        this.step2UndefinedError = false;
        this.experiencesError = false;

        // Check if any field in the experience form has a value
        if (!skipFormValidation && (this.experienceForm.valid && this.experienceForm.get('role')?.value || 
            this.experienceForm.get('company')?.value || 
            this.experienceForm.get('startDate')?.value || 
            this.experienceForm.get('endDate')?.value || 
            this.experienceForm.get('responsibilities')?.value)) { 
             // Call to add experience

            if (!this.addExperience()) { // Proceed only if the experience form is valid
                // Additional logic can be added here if needed
                this.loading =  false; // Set loading state to true
                this.experiencesError = true;
                return false;
            }
        }

        

        // Check if experiences is empty and set error message if it is
        // if (this.experiences.length === 0) {
        //     this.experiencesError = true; // Set specific error variable to true
        //     this.loading = false; // Stop loading if there are no experiences
        //     console.error('Error: No experiences to save'); // Log error message
        //     return; // Exit the method early
        // }

        // Send experiences to the API for saving
        this.http.post(`${environment.url}/api/job-experience`, this.experiences, {
            headers: {
                'Content-Type': 'application/json', // Set content type to JSON
                'Authorization': `Bearer ${accessToken}` // Use access token as authorization header
            }
        })
        .subscribe({
            next: (data: any) => {
                if (data.error) {
                    console.error('Error:', data.error); // Log any errors from the response
                } else {
                    console.log('All experiences saved successfully:', data); // Log success message
                    this.loading = false; 
                    this.progressSaved = !goNext;

                    if (goNext) {

                        window.scrollTo({
                            top: 0,
                            behavior: 'smooth' // Smooth scroll to the top
                        });

                        this.step++; // Increment step
                        
                        document.cookie = "manualStep=" + this.step + "; path=/"; // Update step in cookie
                        
                    }
                }
            },
            error: (error) => {
                console.error('Error:', error); 
                this.step2UndefinedError = true;
                this.loading = false; 
            }
        });
        return true;
    }

    // Method to remove an experience from the list
    removeExperience(experience: any) {
        this.experiences = this.experiences.filter(exp => exp !== experience); // Filter out the experience to be removed
    }

    // Method to open the edit experience modal
    openRegisterModal(experience: any) {
        this.assignUniqueTempId(experience); // Assign a unique temp ID to the experience
        this.dialog.open(EditExperienceModalComponent, {
          ...this.editDialogConfig,
          data: { experience: experience }
        }).afterClosed().subscribe((result: any) => {
            if (result) {
                this.experiences = this.experiences.map(exp => exp.tempId === experience.tempId ? result : exp);
            }
        }); 
      }     

    // Method to save the edited experience
    saveEditExperience() {
        this.editExperienceModal = null;
    }

    private assignUniqueTempId(dict: any): void {
        const generateTempId = () => Math.floor(Math.random() * 1000000); // Generate a random temp ID
        const tempIdSet = new Set(this.experiences.map(exp => exp.tempId)); // Create a set of existing temp IDs

        dict.tempId = generateTempId(); // Initialize tempId
        while (tempIdSet.has(dict.tempId)) { // Ensure the temp ID is not repeated
            dict.tempId = generateTempId(); // Generate a new temp ID
        }
    }

    addEducation() {
        if (this.educationForm.valid) {
            this.missingEducationError = false;
            this.educationsFormError = false;
            const educationData = {
                title: this.educationForm.get('degree')?.value,
                institution: this.educationForm.get('institution')?.value,
                start_date: this.educationForm.get('startDate')?.value,
                end_date: this.educationForm.get('endDate')?.value || null // Treat as ongoing if omitted
            };
            this.educations.push(educationData);
            this.clearForm(this.educationForm); // Clear the form values
            return true;
        } else {
            this.markAllAsTouched(this.educationForm); // Mark all fields as touched if the form is invalid
            this.educationsFormError = true;
            return false;
        }
    }   

    removeEducation(education: any) {
        this.educations = this.educations.filter(edu => edu !== education); // Filter out the education to be removed
        this.missingEducationError = this.educations.length === 0;
    }   

     // Method to open the edit experience modal
     openEditEducationModal(education: any) {
        this.assignUniqueTempId(education); // Assign a unique temp ID to the experience
        this.dialog.open(EditEducationModalComponent, {
          ...this.editDialogConfig,
          data: { education: education }
        }).afterClosed().subscribe((result: any) => {
            if (result) {
                this.educations = this.educations.map(edu => edu.tempId === education.tempId ? result : edu);
            }
        }); 
      } 

    // Method to proceed to the next step in the education process
    nextStep3(goNext: boolean) {    
        const accessToken = getCookie('access_token'); // Extract access token from cookie
        this.loading = true; // Set loading state to true
        this.saveError = false;

        // Check if any field in the education form has a value
        if (!skipFormValidation && (this.educationForm.valid && this.educationForm.get('degree')?.value || 
            this.educationForm.get('degree')?.value ||
            this.educationForm.get('institution')?.value || 
            this.educationForm.get('startDate')?.value || 
            this.educationForm.get('endDate')?.value)) { 
            // Call to add education
            if (!this.addEducation()) { // Proceed only if the education form is valid
                // Additional logic can be added here if needed
                this.loading =  false; // Set loading state to true
                this.educationsError = true;
                return false;
            }

        }

        // Check if educations is empty and set error variable if so
        // if (this.educations.length === 0) {
        //     this.missingEducationError = true; // Set specific error variable to true
        //     this.loading = false; // Stop loading if there are no educations
        //     return; // Exit the method early
        // }


        
        // Send education data to the API for saving
        this.http.post(`${environment.url}/api/education`, this.educations, {
            headers: {
                'Content-Type': 'application/json', // Set content type to JSON
                'Authorization': `Bearer ${accessToken}` // Use access token as authorization header
            }
        })
        .subscribe({
            next: (data: any) => {
                if (data.error) {
                    console.error('Error:', data.error); // Log any errors from the response
                } else {
                    console.log('All education records saved successfully:', data); // Log success message
                    this.loading = false; 
                    this.progressSaved = !goNext;

                    if (goNext) {
                        
                        window.scrollTo({
                            top: 0,
                            behavior: 'smooth' // Smooth scroll to the top
                        });

                        this.step++; // Increment step
                        document.cookie = "manualStep=" + this.step + "; path=/"; // Update step in cookie to be accessible from any page
                        
                    }
                }
            },
            error: (error) => {
                console.error('Error:', error); 
                this.saveError = true;
                this.loading = false; 
            }
        });
        return true;
    }

    addLanguage() {
        if (this.skillsForm.valid) {
            this.missingLanguagesError = false;
            this.addedLanguages.push(this.skillsForm.value);
            this.clearForm(this.skillsForm); // Clear the form values
            return true;
        } else {
            this.markAllAsTouched(this.skillsForm);
            return false;
        }
    }

    openEditLanguageModal(language: any) {
        this.dialog.open(EditLanguageModalComponent, {
          ...this.editDialogConfig,
          data: { language: language }
        }).afterClosed().subscribe((result: any) => {
            if (result) {
                this.addedLanguages = this.addedLanguages.map(lang => lang === language ? result : lang);
            }
        }); 
      }

    addSoftSkill() {
        
        if (this.softSkillsForm.valid) {
            this.missingSoftSkillsError = false;
            this.addedSoftSkills.push(this.softSkillsForm.value);
            this.clearForm(this.softSkillsForm); // Clear the form values
            return true;
        } else {
            this.markAllAsTouched(this.softSkillsForm);
            return false;
        }
    }   


      
    addTechnicalSkill() {
        if (this.technicalSkillsForm.valid) {
            this.missingTechnicalSkillsError = false;
            this.addedTechnicalSkills.push(this.technicalSkillsForm.value);
            this.clearForm(this.technicalSkillsForm); // Clear the form values
            return true;
        } else {
            this.markAllAsTouched(this.technicalSkillsForm);
            return false;
        }
    }   

    openEditTechnicalSkillsModal(skill: any) {
        this.dialog.open(EditTechnicalSkillsModalComponent, {
          ...this.editDialogConfig,
          data: { skill: skill }
        }).afterClosed().subscribe((result: any) => {
            if (result) {
                this.addedTechnicalSkills = this.addedTechnicalSkills.map(item => item === skill ? result : item);
            }
        }); 
    }

    // Soft skills are picked without a level. The API still expects one, so every pick is sent as
    // "advanced" (if they choose it, it is one of their strengths). The level can go once the API drops it.
    readonly maxSoftSkills = 5;
    private readonly softSkillDefaultLevel = skillLevels.find(level => level.level === 'expert');

    isSoftSkillSelected(skill: any): boolean {
        return this.addedSoftSkills.some(item => item.softSkill?.id === skill.id);
    }

    toggleSoftSkill(skill: any) {
        if (this.isSoftSkillSelected(skill)) {
            this.addedSoftSkills = this.addedSoftSkills.filter(item => item.softSkill?.id !== skill.id);
        } else if (this.addedSoftSkills.length < this.maxSoftSkills) {
            this.addedSoftSkills = [...this.addedSoftSkills, { softSkill: skill, softSkillLevel: this.softSkillDefaultLevel }];
            this.missingSoftSkillsError = false;
        }
    }

    removeSoftSkill(skill: any) {
        this.addedSoftSkills = this.addedSoftSkills.filter(item => item !== skill);
        this.missingSoftSkillsError = this.addedSoftSkills.length === 0;
    }

    removeTechnicalSkill(skill: any) {
        this.addedTechnicalSkills = this.addedTechnicalSkills.filter(item => item !== skill);
        this.missingTechnicalSkillsError = this.addedTechnicalSkills.length === 0;
    }

    removeLanguage(language: any) {
        this.addedLanguages = this.addedLanguages.filter(lang => lang !== language);
        this.missingLanguagesError = this.addedLanguages.length === 0;
    }

    nextStep4(goNext: boolean) {
        this.loading = true;
        this.errorSkills = false;
        this.saveError = false;

        // Check if the skills form has any value
        if (!skipFormValidation && (this.skillsForm.valid && this.skillsForm.get('language')?.value || this.skillsForm.get('language')?.value || this.skillsForm.get('languageLevel')?.value)) {
            if (!this.addLanguage()) {
                this.errorSkills = true;    
                this.loading = false;
                return false;
            }
        }

        if (!skipFormValidation && (this.softSkillsForm.valid && this.softSkillsForm.get('softSkill')?.value || this.softSkillsForm.get('softSkill')?.value || this.softSkillsForm.get('softSkillLevel')?.value)) {
            if (!this.addSoftSkill()) {
                this.errorSkills = true;
                this.loading = false;
                return false;
            }
        }

        if (!skipFormValidation && (this.technicalSkillsForm.valid && this.technicalSkillsForm.get('technicalSkill')?.value || this.technicalSkillsForm.get('technicalSkill')?.value || this.technicalSkillsForm.get('technicalSkillLevel')?.value)) {
            if (!this.addTechnicalSkill()) {
                this.errorSkills = true;
                this.loading = false;
                return false;
            }
        }

        if (skipFormValidation || this.addedLanguages.length > 0 || this.addedSoftSkills.length > 0 || this.addedTechnicalSkills.length > 0) {

            const payload = {
                languages: this.addedLanguages.map(lang => ({ language: lang.language.id, level: lang.languageLevel.id })),
                softSkills: this.addedSoftSkills.map(skill => ({ softSkill: skill.softSkill.id, softSkillLevel: skill.softSkillLevel.level })),
                technicalSkills: this.addedTechnicalSkills.map(skill => ({ technicalSkill: skill.technicalSkill, technicalSkillLevel: skill.technicalSkillLevel.level }))
            };

            this.http.post(`${environment.url}/api/skills/batch`, payload, {
                headers: {
                    'Authorization': `Bearer ${getCookie('access_token')}`
                }
            }).subscribe({
                next: () => {
                    this.loading = false; // End loading on success
                    this.progressSaved = !goNext;

                    if (goNext) {

                        window.scrollTo({
                            top: 0,
                            behavior: 'smooth' // Smooth scroll to the top
                        });
                        
                        this.step++; // Increment step
                        document.cookie = "manualStep=" + this.step + "; path=/"; // Update step in cookie to be accessible from any page
                        
                        this.router.navigate(['/session/candidate-profiling']);
                        
                    }

                },
                error: (error) => {
                    console.error('Error:', error);
                    this.saveError = true;
                    this.loading = false; // End loading on error
                }
            });
        }
        else
        {
            this.loading = false;
            this.missingLanguagesError = this.addedLanguages.length === 0;
            this.missingSoftSkillsError = this.addedSoftSkills.length === 0;
            this.missingTechnicalSkillsError = this.addedTechnicalSkills.length === 0;
            
        }
        return true;
    }   

    // Switch to the LinkedIn sign-up (faster): same as the LinkedIn button on the register page.
    // The manual step cookie is cleared so the rest of the flow is the LinkedIn one.
    switchToLinkedin() {
        document.cookie = 'manualStep=; path=/; max-age=0';
        window.location.href = environment.linkedinUrl;
    }

    goBack() {
        this.step--;
        this.progressSaved = false;
        document.cookie = "manualStep=" + this.step + "; path=/"; // Update step in cookie to be accessible from any page
        window.scrollTo({
            top: 0,
            behavior: 'smooth' // Smooth scroll to the top
        });
    }

    goToStep(step: number) {
        if (step <= this.step) {
            this.step = step;
            this.progressSaved = false;
            document.cookie = "manualStep=" + this.step + "; path=/"; // Update step in cookie to be accessible from any page
            if (step == 5) {
                document.cookie = "manualStep=" + 1 + "; path=/"; // Update step in cookie to be accessible from any page
                this.router.navigate(['/session/candidate-profiling']);
            }
        }
    }
    // Empty a form after adding its item to the list. Values go back to '' (not null, as reset() does)
    // so native selects show their placeholder again and empty dates stay invalid.
    private clearForm(form: FormGroup) {
        const empty: { [key: string]: string } = {};
        Object.keys(form.controls).forEach(key => empty[key] = '');
        form.reset(empty);
    }

    // Helper method to mark all form controls as touched
    private markAllAsTouched(form: any) {
        Object.values(form.controls).forEach((control: any) => {
          if (control instanceof FormGroup) {
            Object.values(control.controls).forEach(innerControl => innerControl.markAsTouched()); // Mark inner controls as touched
          } else {
            control.markAsTouched(); // Mark control as touched
          }
        });
    }


}
